import { useEffect, useState } from 'react';
import { Badge, Button, Card, chipClass, EmptyState, fieldClass, IconClose, labelClass, LoadingList, Notice, PageHeader, SectionHeader } from '@timely/ui';
import { EMAIL_RUN_REASONS, describeEmailRun, holds, type EmailRunCategory } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { supabase } from '../lib/supabaseClient';
import { ApiError } from '../lib/api';
import {
    AlertSettingsError, saveAlertSettings, sendTestDigest, useAlertSettings, useScheduledSending,
    type AlertSettings, type EmailRun,
} from '../alerts/useAlertSettings';

interface RoleOption { id: string; name: string }

/** A moment, in the company's zone, as "23 Sept 2026, 08:01". */
function momentIn(value: string, locale: string, timeZone: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(d);
    } catch {
        return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    }
}

/**
 * Reminder digest: the schedule, and the honest record of what it did.
 *
 * **Saving configures; the scheduler sends.** Every attempt the scheduler
 * makes is listed below with what actually happened — sent, partly sent,
 * skipped, failed, retrying — and the list says so when it cannot be read,
 * rather than looking empty. A setting saved is not a message sent, and
 * nothing on this screen is allowed to look like the second when it is only
 * the first.
 *
 * **Whether the scheduler runs at all** is a platform switch, not a company
 * setting. While it is off, a saved schedule sends nothing, and the screen
 * says so at the top and in every confirmation (`/api/alerts/status`).
 */
export function Alerts() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canManage = holds(context, 'integrations.manage');

    const {
        settings: loaded, runs, hasMoreRuns, runsError, preview, loading, error, reload, loadMoreRuns,
    } = useAlertSettings(workspace.id);
    const [draft, setDraft] = useState<AlertSettings>(loaded);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [newRecipient, setNewRecipient] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const scheduled = useScheduledSending(workspace.id, canManage);

    useEffect(() => { setDraft(loaded); }, [loaded]);
    useEffect(() => {
        void supabase().from('workspace_roles').select('id, name').eq('workspace_id', workspace.id)
            .then(({ data }) => setRoles((data as RoleOption[] | null) ?? []));
    }, [workspace.id]);

    const save = async () => {
        setSaving(true);
        setNotice(null);
        try {
            await saveAlertSettings(workspace.id, draft);
            await reload();
            setNotice({
                tone: 'ok',
                text: !draft.dailyEmail
                    ? 'Saved. The daily digest is off.'
                    : scheduled === false
                        ? `Saved for ${draft.sendAtLocal} (${workspace.timezone}). Scheduled sending is switched off for now, so nothing will be sent on this schedule until it is switched on.`
                        : `Saved. The digest goes out daily at ${draft.sendAtLocal} (${workspace.timezone}); each send appears below.`,
            });
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof AlertSettingsError ? err.message : 'That did not work.' });
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        setNotice(null);
        try {
            const result = await sendTestDigest(workspace.id);
            if (result.status === 'sent') {
                setNotice({ tone: 'ok', text: `A test digest was sent to ${result.to}.` });
            } else {
                const reason = result.category && result.category in EMAIL_RUN_REASONS
                    ? EMAIL_RUN_REASONS[result.category as EmailRunCategory]
                    : 'It could not be sent.';
                setNotice({ tone: 'error', text: `The test was not sent. ${reason}` });
            }
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof ApiError ? err.message : 'That did not work.' });
        } finally {
            setTesting(false);
        }
    };

    if (!canManage) {
        return (
            <Page width="form">
                <PageHeader title="Reminder emails" subtitle="Set up by whoever manages your integrations." />
                <EmptyState title="Nothing to show you here" hint="Ask an admin about your company's reminder-email settings." className="!px-0" />
            </Page>
        );
    }

    return (
        <Page width="form">
            <PageHeader
                title="Reminder emails"
                subtitle={scheduled === false
                    ? `A daily email of the day's work, at the time below in ${workspace.timezone}, once scheduled sending is switched on.`
                    : `A daily email of the day's work, sent at the time below in ${workspace.timezone}. Saving changes the schedule; it does not send anything by itself.`}
            />

            {scheduled === false && (
                <Notice tone="warn" role="status" className="mb-4" title="Scheduled sending is switched off for now">
                    You can set the schedule and recipients here, and a test send works, but no daily digest goes out
                    on its own until Timely Payment switches scheduled sending on. Nothing below has been sent on a schedule.
                </Notice>
            )}
            {notice && <div ><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            {loading ? (
                <Card className="mt-5"><LoadingList label="Loading your alert settings" rows={4} /></Card>
            ) : (
                <Card className="mt-5 p-5">
                    <label className="flex items-center gap-3 min-h-9 cursor-pointer mb-5">
                        <input
                            type="checkbox" checked={draft.dailyEmail}
                            onChange={e => setDraft(d => ({ ...d, dailyEmail: e.target.checked }))}

                        />
                        <span className="text-body font-semibold text-label">Send the daily digest</span>
                    </label>

                    <label htmlFor="digest-send-at" className={labelClass}>
                        Send at ({workspace.timezone})
                    </label>
                    {/* A list of times in the product's own select, not the browser's time field and its clock icon
                        (2026-10 global sweep). Every half hour, and the saved time if it is between them. */}
                    <select
                        id="digest-send-at" value={draft.sendAtLocal}
                        onChange={e => setDraft(d => ({ ...d, sendAtLocal: e.target.value }))}
                        className={fieldClass({ className: 'w-40 mb-5' })}
                    >
                        {sendTimes(draft.sendAtLocal).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <span className={labelClass}>Recipients, by role</span>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {roles.map(role => {
                            const on = draft.recipientRoleIds.includes(role.id);
                            return (
                                <button
                                    key={role.id} type="button" aria-pressed={on}
                                    onClick={() => setDraft(d => ({
                                        ...d,
                                        recipientRoleIds: on
                                            ? d.recipientRoleIds.filter(id => id !== role.id)
                                            : [...d.recipientRoleIds, role.id],
                                    }))}
                                    className={chipClass(on)}
                                >
                                    {role.name}
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-secondary text-label-3 mb-5">
                        Everyone receives their own view: somebody who sees only their own accounts in the app
                        gets a digest of only their own accounts.
                    </p>

                    <label htmlFor="digest-extra" className={labelClass}>Also send to</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {draft.extraRecipients.map(email => (
                            <Badge key={email} tone="neutral">
                                {email}{' '}
                                <button
                                    type="button" aria-label={`Remove ${email}`}
                                    className="-mr-0.5 grid place-items-center text-label-3 hover:text-label"
                                    onClick={() => setDraft(d => ({ ...d, extraRecipients: d.extraRecipients.filter(e => e !== email) }))}
                                ><IconClose size={12} /></button>
                            </Badge>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-1">
                        <input
                            id="digest-extra" type="email" value={newRecipient} placeholder="teammate@example.com"
                            onChange={e => setNewRecipient(e.target.value)}
                            className={fieldClass({ className: 'flex-1 min-w-0' })}
                        />
                        <Button
                            variant="secondary" size="sm"
                            onClick={() => {
                                const email = newRecipient.trim().toLowerCase();
                                if (!email) return;
                                setDraft(d => d.extraRecipients.includes(email) ? d : { ...d, extraRecipients: [...d.extraRecipients, email] });
                                setNewRecipient('');
                            }}
                        >Add</Button>
                    </div>
                    <p className="text-secondary text-label-3 mb-5">
                        The sign-in address of an active member of this company. Anyone else is skipped, and a
                        member who is suspended or removed stops receiving it without anyone editing this list.
                    </p>

                    <label className="flex items-center gap-3 min-h-9 cursor-pointer mb-4">
                        <input
                            type="checkbox" checked={draft.skipWhenEmpty}
                            onChange={e => setDraft(d => ({ ...d, skipWhenEmpty: e.target.checked }))}

                        />
                        <span className="text-body text-label-2">Don’t send when someone has nothing to chase</span>
                    </label>

                    {preview && (
                        <p className="text-secondary text-label-2 mb-5" data-testid="recipient-preview">
                            {`As saved, today's digest would go to ${preview.memberRecipients} ${preview.memberRecipients === 1 ? 'person' : 'people'}.`}
                            {preview.unmatchedExtras > 0 && ` ${preview.unmatchedExtras} extra ${preview.unmatchedExtras === 1 ? 'address is' : 'addresses are'} not an active member and will be skipped.`}
                        </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <Button variant="primary" onClick={() => { void save(); }} disabled={saving} busy={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button variant="secondary" onClick={() => { void test(); }} disabled={testing || saving} busy={testing}>
                            {testing ? 'Sending…' : 'Send a test to me'}
                        </Button>
                    </div>
                </Card>
            )}

            <SectionHeader title="Recent sends" className="mt-8" />
            <Card className="mt-3 overflow-hidden">
                {runsError ? (
                    <div className="px-5 py-4"><FormNotice tone="error">{runsError}</FormNotice></div>
                ) : runs.length === 0 ? (
                    <EmptyState
                        compact
                        title="No digests yet"
                        hint="Each daily send appears here — sent, skipped or failed — along with any tests you send."
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {runs.map(run => <RunRow key={run.id} run={run} locale={workspace.locale} timeZone={workspace.timezone} />)}
                    </ul>
                )}
                {hasMoreRuns && !runsError && (
                    <div className="px-5 py-3">
                        <Button variant="secondary" size="sm" onClick={() => { void loadMoreRuns(); }}>Show earlier sends</Button>
                    </div>
                )}
            </Card>
        </Page>
    );
}

/** The digest's send times: every half hour, with the saved one kept if it falls between them. */
function sendTimes(saved: string): string[] {
    const times = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
    const current = /^\d{2}:\d{2}/.test(saved) ? saved.slice(0, 5) : null;
    return current && !times.includes(current) ? [...times, current].sort() : times;
}

function RunRow({ run, locale, timeZone }: { run: EmailRun; locale: string; timeZone: string }) {
    const described = describeEmailRun(run);
    return (
        <li className="px-5 py-3" data-testid="email-run">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-body font-semibold text-label">
                    {run.kind === 'test' ? 'Test' : 'Daily digest'} · {momentIn(run.at, locale, timeZone)}
                </span>
                <Badge variant="text" dot tone={described.tone}>{described.label}</Badge>
            </div>
            {described.explanation && (
                <p className="mt-1 text-secondary text-label-3">{described.explanation}</p>
            )}
            {run.recipients > 0 && run.status !== 'sent' && (
                <p className="mt-0.5 text-caption text-label-3">
                    {run.recipients} {run.recipients === 1 ? 'recipient' : 'recipients'} · {run.delivered} delivered
                    {run.failed > 0 ? ` · ${run.failed} failed` : ''}
                </p>
            )}
            {run.nextAttemptAt && (
                <p className="mt-0.5 text-caption text-label-3">Next attempt {momentIn(run.nextAttemptAt, locale, timeZone)}</p>
            )}
        </li>
    );
}
